import React, { useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import logosImage from 'assets/images/qr_redirect/qr_redirect_logos.jpeg'
import logosImageDark from 'assets/images/qr_redirect/qr_redirect_logos_dark.png'
import defaultImage from 'assets/images/qr_redirect/qr_redirect_default.jpeg'
import downloadIos from 'assets/images/qr_redirect/download_ios.svg'
import { Image } from 'semantic-ui-react'
import ViewYoutubeMedia from 'app/protected/common/ViewYoutubeMedia'
import ViewYoutubePlaylistMedia from 'app/protected/common/ViewYoutubePlaylistMedia'
import AndroidButton from './AndroidButton'

import i18next from 'i18next'

const GA_SAFE_PARAMS = [
  'ac',
  'referer-type',
  'url-video',
  'url-video-list',
  'url-video-si',
] as const

const pickSafeParams = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search)
  const safe: Record<string, string> = {}
  for (const key of GA_SAFE_PARAMS) {
    const value = params.get(key)
    if (value) safe[key.replaceAll('-', '_')] = value
  }
  return safe
}

const getRedactedUrl = (location: { pathname: string }): string => {
  return `${globalThis.location?.origin ?? ''}${location.pathname}`
}

interface CnDownloadsProps {
  mobileAppGaTrackingId?: string
}

interface CnDownloadsState {
  iosLink: string
  androidLink: string
  urlVideo: string | null
  urlVideoList: string | null
  urlVideoSi: string | null
  refererType: string | null
}

export default function CnDownloads({
  mobileAppGaTrackingId,
}: Readonly<CnDownloadsProps>) {
  const [state, setState] = useState<CnDownloadsState>({
    iosLink: '',
    androidLink: '',
    urlVideo: null,
    urlVideoList: null,
    urlVideoSi: null,
    refererType: null,
  })
  const location = useLocation()
  const [isDarkMode, setIsDarkMode] = useState(false)

  // Function to get query parameters
  const getQueryParams = (search: string) => {
    return new URLSearchParams(search)
  }

  const textRefiOS = useRef<HTMLTextAreaElement>(null)
  const textRefAndroid = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (globalThis.gtag && mobileAppGaTrackingId) {
      const safeParams = pickSafeParams(location.search)
      globalThis.gtag('event', 'def_deep_link_site_launch_token_login', {
        url: getRedactedUrl(location),
        app: safeParams['ac'],
        send_to: [mobileAppGaTrackingId],
      })
      globalThis.gtag('event', 'qrredirect', {
        ...safeParams,
        send_to: [mobileAppGaTrackingId],
      })
    }

    const mediaQuery = globalThis.matchMedia?.('(prefers-color-scheme: dark)')

    if (!mediaQuery) {
      setIsDarkMode(false)
      return
    }

    const handleChange = (event: MediaQueryListEvent) => {
      setIsDarkMode(event.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    setIsDarkMode(mediaQuery.matches)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  useEffect(() => {
    const queryParams = getQueryParams(location.search)
    const refererLinkEncoded = queryParams.get('referer-link')
    const refererType = queryParams.get('referer-type')
    const urlVideo = queryParams.get('url-video')
    const urlVideoList = queryParams.get('url-video-list')
    const urlVideoSi = queryParams.get('url-video-si')

    // Decode the Base64 encoded referer-link, treating invalid input as null
    let refererLink: string | null = null
    if (refererLinkEncoded) {
      try {
        refererLink = atob(refererLinkEncoded)
      } catch {
        refererLink = null
      }
    }

    const iosBaseUrl = 'https://lfconnect.app.link/redirect'
    const iosParams = new URLSearchParams()
    if (refererType) {
      iosParams.set('deep_link_type', refererType)
    }
    if (refererLink) {
      iosParams.set('deep_link_value', refererLink)
    }
    const iosLink = iosParams.toString()
      ? `${iosBaseUrl}?${iosParams.toString()}`
      : iosBaseUrl

    const androidLink = 'https://assets.lfconnect.com/cn/android/lfc-latest.apk'

    setState({
      iosLink: iosLink,
      androidLink: androidLink,
      urlVideo,
      urlVideoList,
      urlVideoSi,
      refererType: refererType,
    })
  }, [location.search])

  const handleAndroidButtonClick = () => {
    if (globalThis.gtag && mobileAppGaTrackingId) {
      const safeParams = pickSafeParams(location.search)
      globalThis.gtag('event', 'def_deep_link_site_click_token_login', {
        url: getRedactedUrl(location),
        app: safeParams['ac'],
        platform: 'ANDROID',
        send_to: [mobileAppGaTrackingId],
      })
    }
    globalThis.open(state.androidLink, '_blank', 'noopener,noreferrer')
  }

  const handleiOSButtonClick = async () => {
    if (globalThis.gtag && mobileAppGaTrackingId) {
      const safeParams = pickSafeParams(location.search)
      globalThis.gtag('event', 'def_deep_link_site_click_token_login', {
        url: getRedactedUrl(location),
        app: safeParams['ac'],
        platform: 'iOS',
        send_to: [mobileAppGaTrackingId],
      })
    }
    if (globalThis.navigator?.clipboard) {
      try {
        await globalThis.navigator.clipboard.writeText(state.iosLink)
      } catch (error) {
        console.log('Error copying to clipboard', error)
      }
    }
    globalThis.open(state.iosLink, '_blank', 'noopener,noreferrer')
  }

  let mediaContent = (
    <Image
      size="large"
      src={defaultImage}
      alt={i18next.t('workout')}
      style={{
        width: '100%',
      }}
    />
  )

  if (state.urlVideo) {
    mediaContent = <ViewYoutubeMedia youtubeTag={state.urlVideo} />
  } else if (state.urlVideoList) {
    mediaContent = (
      <ViewYoutubePlaylistMedia
        listId={state.urlVideoList}
        siParam={state.urlVideoSi}
      />
    )
  }

  const backgroundColorClass = isDarkMode ? 'bg-black' : 'bg-white'
  const logoImage = isDarkMode ? logosImageDark : logosImage

  return (
    <div className={`flex justify-center ${backgroundColorClass}`}>
      <div className={`h-fit w-full text-center ${backgroundColorClass}`}>
        <Image
          size="large"
          src={logoImage}
          style={{
            paddingTop: '8px',
            width: '100%',
          }}
          alt={i18next.t('logo')}
        />
        <div className="mb-5">{mediaContent}</div>

        {(state.refererType === 'CARDIO_SUMMARY' ||
          state.refererType === 'SSO') && (
          <div className="mx-[49px] mb-5 flex flex-col items-center text-center text-[16px] font-normal leading-[22px] tracking-[0.16px]">
            <span>{i18next.t('qr_redirect_cardio_text1')}</span>
            <span>
              {i18next.t('qr_redirect_cardio_text2')}{' '}
              <span className="text-[16px] font-bold leading-[22px] tracking-[0.16px]">
                {i18next.t('qr_redirect_cardio_text3')}
              </span>
              {':'}
            </span>
            <div className="mt-6 flex w-fit flex-col text-left text-[16px] font-normal leading-[22px] tracking-[0.16px]">
              <span>{i18next.t('qr_redirect_cardio_text4')}</span>
              <span>{i18next.t('qr_redirect_cardio_text5')}</span>
              <span>{i18next.t('qr_redirect_cardio_text6')}</span>
            </div>
          </div>
        )}
        {state.refererType === 'STRENGTH' && (
          <div className="mx-[49px] mb-5 flex flex-col items-center text-center text-[16px] font-normal leading-[22px] tracking-[0.16px]">
            <span>{i18next.t('qr_redirect_strength_text1')}</span>
            <div className="mt-6 flex w-fit flex-col text-left text-[16px] font-normal leading-[22px] tracking-[0.16px]">
              <span>{i18next.t('qr_redirect_strength_text2')}</span>
            </div>
          </div>
        )}

        <div className="flex flex-col justify-center">
          <button
            onClick={handleiOSButtonClick}
            className="border-0 bg-transparent p-0"
          >
            <Image
              size="large"
              src={downloadIos}
              alt={i18next.t('download')}
              style={{
                width: '50%',
                display: 'inline',
              }}
            />
          </button>

          <AndroidButton handleAndroidButtonClick={handleAndroidButtonClick} />
        </div>
        <textarea
          className={`h-px resize-none overflow-y-hidden border-0 p-0 text-brand-white ${isDarkMode ? 'bg-black' : 'bg-brand-white'}`}
          value={state.iosLink}
          ref={textRefiOS}
          readOnly
        />
        <textarea
          className={`h-px resize-none overflow-y-hidden border-0 p-0 text-brand-white ${isDarkMode ? 'bg-black' : 'bg-brand-white'}`}
          value={state.androidLink}
          ref={textRefAndroid}
          readOnly
        />
      </div>
    </div>
  )
}
